import React, { useState, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/Dialog';
import { Badge } from '../ui/Badge';
import { 
  User, 
  Upload, 
  Download, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  FileText,
  X,
  Plus,
  MapPin,
  Activity,
  Phone,
  Mail,
  CreditCard,
  Eye,
  Info
} from 'lucide-react';
import { apiService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import CountryCodeSelector from '../ui/CountryCodeSelector';
import { 
  validateDriverCsvStructure, 
  validateDriverData, 
  parseCsvFile, 
  generateDriverCsvTemplate, 
  downloadCsvFile 
} from '../../utils/csvValidation';

const BulkDriverRegistrationModal = ({ onSuccess, onError, children, inline = false, onCancel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationMode, setRegistrationMode] = useState('manual');
  const [drivers, setDrivers] = useState([]);
  const [errors, setErrors] = useState({});
  const [csvData, setCsvData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [csvValidationErrors, setCsvValidationErrors] = useState([]);
  const { user } = useAuth();

  const addDriver = () => {
    setDrivers(prev => [...prev, {
      name: '',
      phone: '',
      phone_country_code: '+91',
      email: '',
      password: '',
      license_number: '',
      experience_level: 'BEGINNER',
      current_latitude: '',
      current_longitude: '',
      availability_status: 'AVAILABLE'
    }]);
  };

  const removeDriver = (index) => {
    setDrivers(prev => prev.filter((_, i) => i !== index));
  };

  const updateDriver = useCallback((index, field, value) => {
    setDrivers(prev => prev.map((driver, i) => 
      i === index ? { ...driver, [field]: value } : driver
    ));
  }, []);

  const validateDrivers = () => {
    const newErrors = {};
    
    if (drivers.length === 0) {
      newErrors.general = 'Please add at least one driver';
      setErrors(newErrors);
      return false;
    }
    
    drivers.forEach((driver, index) => {
      if (!driver.name.trim()) {
        newErrors[`driver_${index}_name`] = 'Driver name is required';
      } else if (driver.name.trim().length < 2) {
        newErrors[`driver_${index}_name`] = 'Name must be at least 2 characters long';
      }
      
      if (!driver.phone.trim()) {
        newErrors[`driver_${index}_phone`] = 'Phone number is required';
      } else if (!/^[6-9]\d{9}$/.test(driver.phone.trim())) {
        newErrors[`driver_${index}_phone`] = 'Please enter a valid 10-digit Indian mobile number';
      }
      
      if (driver.email && driver.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(driver.email.trim())) {
          newErrors[`driver_${index}_email`] = 'Please enter a valid email address';
        }
      }
      
      if (!driver.password.trim()) {
        newErrors[`driver_${index}_password`] = 'Password is required';
      } else if (driver.password.trim().length < 8) {
        newErrors[`driver_${index}_password`] = 'Password must be at least 8 characters';
      }
      
      if (!driver.license_number.trim()) {
        newErrors[`driver_${index}_license_number`] = 'License number is required';
      } else if (driver.license_number.trim().length < 5) {
        newErrors[`driver_${index}_license_number`] = 'License number must be at least 5 characters';
      }
      
      if (driver.current_latitude && (isNaN(driver.current_latitude) || driver.current_latitude < -90 || driver.current_latitude > 90)) {
        newErrors[`driver_${index}_current_latitude`] = 'Please enter a valid latitude (-90 to 90)';
      }
      
      if (driver.current_longitude && (isNaN(driver.current_longitude) || driver.current_longitude < -180 || driver.current_longitude > 180)) {
        newErrors[`driver_${index}_current_longitude`] = 'Please enter a valid longitude (-180 to 180)';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🚀 Starting bulk driver registration...');
    console.log('📊 Drivers to register:', drivers.length);
    
    if (!validateDrivers()) {
      console.log('❌ Validation failed');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const operatorId = user?.operator?.operator_id || 
                        user?.operator_id || 
                        user?.operator?.id ||
                        user?.fleet_operator_id ||
                        user?.fleet_operator?.operator_id;
      
      const driversData = drivers.map(driver => ({
        name: driver.name.trim(),
        phone: driver.phone.trim(),
        phone_country_code: driver.phone_country_code || '+91',
        email: driver.email.trim() || null,
        password: driver.password.trim(),
        license_number: driver.license_number.trim().toUpperCase(),
        experience_level: driver.experience_level.toUpperCase(), // ✅ Convert to uppercase
        current_latitude: driver.current_latitude ? parseFloat(driver.current_latitude) : null,
        current_longitude: driver.current_longitude ? parseFloat(driver.current_longitude) : null,
        operator_id: operatorId // ✅ Add operator_id to each driver
      }));
      
      console.log('👤 User object:', user);
      console.log('👤 Operator ID:', operatorId);
      
      if (!operatorId) {
        throw new Error('Operator ID not found. Please ensure you are logged in as a fleet operator.');
      }
      
      console.log('📤 Sending data to API:', { drivers: driversData, operator_id: operatorId });
      
      const result = await apiService.bulkRegisterDrivers({ 
        drivers: driversData,
        operator_id: operatorId
      });
      
      console.log('✅ API response:', result);
      
      if (result.success) {
        onSuccess && onSuccess(result.data);
        setIsOpen(false);
        setDrivers([]);
        setErrors({});
      }
    } catch (error) {
      console.error('Bulk registration error:', error);
      
      let errorMessage = 'Failed to register drivers';
      if (error.message?.includes('already exists')) {
        errorMessage = 'One or more drivers with this information already exist. Please check your data.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      onError && onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (inline && onCancel) {
      onCancel();
    } else {
      setIsOpen(false);
    }
    setDrivers([]);
    setErrors({});
    setRegistrationMode('manual');
    clearCsvData();
  };

  const downloadTemplate = () => {
    const csvContent = generateDriverCsvTemplate();
    downloadCsvFile(csvContent, 'driver_bulk_template.csv');
  };

  const handleFileUpload = (event) => {
    console.log('🔄 CSV file upload started (Driver)');
    const file = event.target.files[0];
    if (!file) {
      console.log('❌ No file selected');
      return;
    }

    console.log('📁 Selected file:', file.name, 'Type:', file.type, 'Size:', file.size);

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      console.log('❌ Invalid file type:', file.type);
      onError && onError('Please select a valid CSV file');
      return;
    }

    setCsvFile(file);
    setUploadProgress(0);
    setCsvValidationErrors([]);

    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        setUploadProgress((e.loaded / e.total) * 100);
      }
    };

    reader.onload = (e) => {
      try {
        console.log('📖 File read completed, parsing CSV (Driver)...');
        const csv = e.target.result;
        console.log('📄 CSV content length:', csv.length);
        
        const { headers, data } = parseCsvFile(csv);
        console.log('📋 Parsed headers:', headers);
        console.log('📊 Parsed data rows:', data.length);
        console.log('📋 First data row:', data[0]);
        console.log('📋 Headers include phone_country_code:', headers.includes('phone_country_code'));
        
        const structureValidation = validateDriverCsvStructure(headers);
        console.log('🔍 Structure validation result:', structureValidation);
        
        if (!structureValidation.isValid) {
          console.log('❌ Structure validation failed:', structureValidation.errors);
          setCsvValidationErrors(structureValidation.errors);
          onError && onError('CSV structure validation failed. Please check the file format.');
          return;
        }
        
        const validationErrors = [];
        data.forEach((row, index) => {
          const rowErrors = validateDriverData(row, index);
          validationErrors.push(...rowErrors);
        });

        console.log('🔍 Data validation errors:', validationErrors.length);

        if (validationErrors.length > 0) {
          console.log('❌ Data validation failed:', validationErrors);
          setCsvValidationErrors(validationErrors);
          onError && onError('CSV data validation failed. Please check the data.');
          return;
        }

        console.log('✅ CSV validation passed, setting data (Driver)...');
        setCsvData(data);
        setPreviewMode(true);
        setUploadProgress(100);
      } catch (error) {
        console.error('❌ CSV processing error (Driver):', error);
        onError && onError(error.message);
      }
    };

    reader.readAsText(file);
  };

  const processCsvData = () => {
    console.log('🔄 Processing CSV data for drivers...');
    console.log('📋 Raw CSV data:', csvData);
    
    const processedDrivers = csvData.map((row, index) => {
      console.log(`📋 Processing row ${index + 1}:`, {
        name: row.name,
        phone: row.phone,
        phone_country_code: row.phone_country_code,
        email: row.email
      });
      
      return {
        name: row.name || '',
        phone: row.phone || '',
        phone_country_code: row.phone_country_code || '+91',
        email: row.email || '',
        password: row.password || '',
        license_number: row.license_number || '',
        experience_level: row.experience_level || 'BEGINNER',
        current_latitude: row.current_latitude || '',
        current_longitude: row.current_longitude || '',
        availability_status: row.availability_status || 'AVAILABLE'
      };
    });

    console.log('✅ Processed drivers:', processedDrivers);
    console.log('🔍 Country codes in processed drivers:', processedDrivers.map(d => d.phone_country_code));
    setDrivers(processedDrivers);
    setPreviewMode(false);
    setRegistrationMode('manual');
  };

  const clearCsvData = () => {
    setCsvData([]);
    setCsvFile(null);
    setPreviewMode(false);
    setUploadProgress(0);
    setCsvValidationErrors([]);
  };

  const csvUploadSection = (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            CSV Upload
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={downloadTemplate}
            className="flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Download Template</span>
          </Button>
        </CardTitle>
        <CardDescription>
          Upload a CSV file with driver data. Download the template to see the required format.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* CSV Structure Information */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Required CSV Structure
          </h4>
          <div className="text-sm text-blue-800 space-y-2">
            <p className="font-medium">Your CSV file must contain these exact columns:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {['name', 'phone', 'phone_country_code', 'email', 'password', 'license_number', 'experience_level', 'current_latitude', 'current_longitude', 'availability_status'].map((header) => (
                <div key={header} className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="text-xs">{header}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {!previewMode ? (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">Drag and drop your CSV file here, or click to browse</p>
              <p className="text-sm text-gray-500 mb-4">Supported format: CSV files only</p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload-drivers"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('csv-upload-drivers').click()}
              >
                Choose File
              </Button>
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Uploading...</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {csvFile && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">{csvFile.name}</p>
                      <p className="text-sm text-blue-700">
                        {(csvFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearCsvData}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {csvValidationErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <h4 className="font-medium text-red-900">Validation Errors</h4>
                </div>
                <div className="max-h-32 overflow-y-auto">
                  <ul className="text-sm text-red-800 space-y-1">
                    {csvValidationErrors.map((error, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-red-600 mt-0.5">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-medium text-gray-900">CSV Preview</h4>
                  <p className="text-xs text-gray-500">First 5 rows of uploaded data</p>
                  <p className="text-xs text-blue-600 mt-1">💡 Experience levels will be automatically converted to uppercase</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{csvData.length} drivers</Badge>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearCsvData}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3">Name</th>
                      <th className="text-left py-2 px-3">Phone</th>
                      <th className="text-left py-2 px-3">Country Code</th>
                      <th className="text-left py-2 px-3">Email</th>
                      <th className="text-left py-2 px-3">License</th>
                      <th className="text-left py-2 px-3">Experience</th>
                      <th className="text-left py-2 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.slice(0, 5).map((row, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-2 px-3">{row.name}</td>
                        <td className="py-2 px-3">{row.phone}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className="text-xs">
                            {row.phone_country_code || '+91'}
                          </Badge>
                        </td>
                        <td className="py-2 px-3">{row.email}</td>
                        <td className="py-2 px-3">{row.license_number}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className="text-xs">
                            {row.experience_level}
                          </Badge>
                        </td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className="text-xs">
                            {row.availability_status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {csvData.length > 5 && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Showing first 5 rows of {csvData.length} total rows
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={clearCsvData}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={processCsvData}
                className="flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Process Data
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const manualEntrySection = (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Drivers to Register</h3>
        <Button
          type="button"
          onClick={addDriver}
          className="flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Driver</span>
        </Button>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-800">{errors.general}</p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {drivers.map((driver, index) => {
          console.log(`🔍 Rendering driver ${index + 1}:`, {
            name: driver.name,
            phone: driver.phone,
            phone_country_code: driver.phone_country_code
          });
          return (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-base">Driver {index + 1}</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeDriver(index)}
                className="text-red-600 hover:text-red-700 h-9 w-9 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Personal Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Full Name *
                    </label>
                    <Input
                      placeholder="Enter driver's full name"
                      value={driver.name}
                      onChange={(e) => updateDriver(index, 'name', e.target.value)}
                      className={`text-base ${errors[`driver_${index}_name`] ? 'border-red-500' : ''}`}
                    />
                    {errors[`driver_${index}_name`] && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                        {errors[`driver_${index}_name`]}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      Phone Number *
                    </label>
                    <div className="flex gap-2">
                      <div className="w-28">
                        <CountryCodeSelector
                          value={driver.phone_country_code}
                          onChange={(e) => {
                            console.log(`🔄 Updating country code for driver ${index + 1}:`, e.target.value);
                            updateDriver(index, 'phone_country_code', e.target.value);
                          }}
                          placeholder="Country"
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          placeholder="9876543210"
                          value={driver.phone}
                          onChange={(e) => updateDriver(index, 'phone', e.target.value)}
                          className={`text-base ${errors[`driver_${index}_phone`] ? 'border-red-500' : ''}`}
                        />
                      </div>
                    </div>
                    {errors[`driver_${index}_phone`] && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                        {errors[`driver_${index}_phone`]}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="driver@example.com"
                      value={driver.email}
                      onChange={(e) => updateDriver(index, 'email', e.target.value)}
                      className={`text-base ${errors[`driver_${index}_email`] ? 'border-red-500' : ''}`}
                    />
                    {errors[`driver_${index}_email`] && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                        {errors[`driver_${index}_email`]}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Password *
                    </label>
                    <Input
                      type="password"
                      placeholder="Enter password"
                      value={driver.password}
                      onChange={(e) => updateDriver(index, 'password', e.target.value)}
                      className={`text-base ${errors[`driver_${index}_password`] ? 'border-red-500' : ''}`}
                    />
                    {errors[`driver_${index}_password`] && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                        {errors[`driver_${index}_password`]}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Professional Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Driving License Number *
                    </label>
                    <Input
                      placeholder="MH0220140001234"
                      value={driver.license_number}
                      onChange={(e) => updateDriver(index, 'license_number', e.target.value)}
                      className={`text-base ${errors[`driver_${index}_license_number`] ? 'border-red-500' : ''}`}
                    />
                    {errors[`driver_${index}_license_number`] && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                        {errors[`driver_${index}_license_number`]}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Experience Level
                    </label>
                    <Select 
                      value={driver.experience_level} 
                      onValueChange={(value) => updateDriver(index, 'experience_level', value)}
                    >
                      <SelectTrigger className="text-base">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BEGINNER">Beginner</SelectItem>
                        <SelectItem value="INTERMEDIATE">Intermediate</SelectItem>
                        <SelectItem value="EXPERIENCED">Experienced</SelectItem>
                        <SelectItem value="EXPERT">Expert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Availability Status
                    </label>
                    <Select 
                      value={driver.availability_status} 
                      onValueChange={(value) => updateDriver(index, 'availability_status', value)}
                    >
                      <SelectTrigger className="text-base">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AVAILABLE">Available</SelectItem>
                        <SelectItem value="BUSY">Busy</SelectItem>
                        <SelectItem value="OFFLINE">Offline</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Current Latitude
                    </label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="19.0760"
                      value={driver.current_latitude}
                      onChange={(e) => updateDriver(index, 'current_latitude', e.target.value)}
                      className={`text-base ${errors[`driver_${index}_current_latitude`] ? 'border-red-500' : ''}`}
                    />
                    {errors[`driver_${index}_current_latitude`] && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                        {errors[`driver_${index}_current_latitude`]}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Current Longitude
                    </label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="72.8777"
                      value={driver.current_longitude}
                      onChange={(e) => updateDriver(index, 'current_longitude', e.target.value)}
                      className={`text-base ${errors[`driver_${index}_current_longitude`] ? 'border-red-500' : ''}`}
                    />
                    {errors[`driver_${index}_current_longitude`] && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                        {errors[`driver_${index}_current_longitude`]}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading || drivers.length === 0}
          className="min-w-[140px]"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Registering...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Register {drivers.length} Driver{drivers.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>
      </div>
    </form>
  );

  if (inline) {
    return (
      <div className="w-full max-w-5xl">
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
          <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
            <User className="w-5 h-5 text-green-600" />
            Bulk Driver Registration
          </h2>
          <p className="text-gray-600">Register multiple drivers at once. Choose between manual entry or CSV upload.</p>
        </div>

        <div className="space-y-6">
          {/* Registration Mode Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Registration Method
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={registrationMode === 'manual' ? 'default' : 'outline'}
                  onClick={() => setRegistrationMode('manual')}
                  className="flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Manual Entry</span>
                </Button>
                <Button
                  type="button"
                  variant={registrationMode === 'csv' ? 'default' : 'outline'}
                  onClick={() => setRegistrationMode('csv')}
                  className="flex items-center space-x-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>CSV Upload</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {registrationMode === 'csv' && csvUploadSection}
          {registrationMode === 'manual' && manualEntrySection}
        </div>
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto mx-4">
        <DialogHeader className="pb-0">
          <DialogTitle className="flex items-center space-x-2 text-xl">
            <User className="w-5 h-5 text-green-600" />
            <span>Bulk Register Drivers</span>
          </DialogTitle>
          <DialogDescription className="pb-4 pl-7">
            Register multiple drivers at once. Choose between manual entry or CSV upload.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Registration Mode Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Registration Method
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={registrationMode === 'manual' ? 'default' : 'outline'}
                  onClick={() => setRegistrationMode('manual')}
                  className="flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Manual Entry</span>
                </Button>
                <Button
                  type="button"
                  variant={registrationMode === 'csv' ? 'default' : 'outline'}
                  onClick={() => setRegistrationMode('csv')}
                  className="flex items-center space-x-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>CSV Upload</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {registrationMode === 'csv' && csvUploadSection}
          {registrationMode === 'manual' && manualEntrySection}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkDriverRegistrationModal;